import { useQuery } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Fuse from "fuse.js";
import { contentApi } from "../api";
import TileCard from "./components/TileCard";
import type { Tile } from "../types";
import { ChevronLeft } from "lucide-react";

interface SearchResult extends Tile {
  department_name: string;
  department_slug: string;
  department_color: string;
  section_name: string;
}

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const { data, isLoading } = useQuery({ queryKey: ["content"], queryFn: contentApi.get });

  const all: SearchResult[] = useMemo(() => {
    if (!data) return [];
    const arr: SearchResult[] = [];
    for (const d of data.departments) {
      for (const s of d.sections) {
        for (const t of s.tiles) {
          arr.push({
            ...t,
            department_name: d.name,
            department_slug: d.slug,
            department_color: d.color,
            section_name: s.name,
          });
        }
      }
    }
    return arr;
  }, [data]);

  const fuse = useMemo(
    () =>
      new Fuse(all, {
        keys: ["name", "description", "tags", "department_name", "section_name"],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [all],
  );

  const results = q ? fuse.search(q).map((r) => r.item) : [];

  const grouped = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; color: string; tiles: SearchResult[] }>();
    for (const r of results) {
      const key = r.department_slug;
      if (!map.has(key)) {
        map.set(key, {
          slug: r.department_slug,
          name: r.department_name,
          color: r.department_color,
          tiles: [],
        });
      }
      map.get(key)!.tiles.push(r);
    }
    return Array.from(map.values());
  }, [results]);

  return (
    <div className="max-w-4xl mx-auto px-8 py-10">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-[12.5px] text-ink-500 hover:text-ink-900 mb-6 transition-colors"
      >
        <ChevronLeft className="size-3.5" /> Back
      </Link>

      <h1 className="display text-[36px] font-medium tracking-tight text-ink-900">Search</h1>
      <p className="text-[14px] text-ink-500 mt-1.5">
        {q ? (
          <>
            <span className="display italic">{results.length}</span>{" "}
            {results.length === 1 ? "result" : "results"} for{" "}
            <span className="font-medium text-ink-800">“{q}”</span>
          </>
        ) : (
          "Type a query in the search bar above."
        )}
      </p>

      {isLoading && <p className="mt-8 text-ink-500 text-[13px]">Loading…</p>}

      {!isLoading && q && results.length === 0 && (
        <div className="surface mt-8 p-10 text-center text-[13px] text-ink-500">
          No matches. Try a different keyword.
        </div>
      )}

      <div className="mt-10 space-y-10">
        {grouped.map((g) => {
          const style = { ["--accent-color" as never]: g.color };
          return (
            <section key={g.slug}>
              <div className="mb-3 flex items-baseline justify-between">
                <Link
                  to={`/d/${g.slug}`}
                  className="display text-[18px] font-medium tracking-tight text-ink-900 hover:text-accent-500 inline-flex items-center gap-2 transition-colors"
                >
                  <span className="dot" style={{ backgroundColor: g.color }} aria-hidden />
                  {g.name}
                </Link>
                <span className="text-[11.5px] text-ink-400 font-mono">{g.tiles.length}</span>
              </div>
              <div
                style={style}
                className="surface accent-top divide-y divide-ink-100 overflow-hidden"
              >
                {g.tiles.map((t) => (
                  <TileCard key={t.id} tile={t} variant="row" deptColor={g.color} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
